import { Component, computed, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationLinkComponent } from '../navigation-link/navigation-link.component';
import { NavigationConfig, NavigationEvent } from '../navigation-link/interfaces/navigation.interfaces';
import { TripNavigationService } from '../../services/trip-navigation.service';
import {
  TripNavigationData,
  TripQueryParams,
  TripNavigationEvent,
  TripDisplayMode,
} from './interfaces/trip-navigation.interfaces';

@Component({
  selector: 'app-trip-navigation',
  standalone: true,
  imports: [CommonModule, NavigationLinkComponent],
  templateUrl: './trip-navigation.component.html',
  styleUrl: './trip-navigation.component.scss',
})
export class TripNavigationComponent {
  private readonly tripNavService = inject(TripNavigationService);

  readonly trip = input<TripNavigationData>();
  readonly displayMode = input<TripDisplayMode>('id');
  readonly customQueryParams = input<TripQueryParams>({});
  readonly openInNewTab = input<boolean>(false);
  readonly customClass = input<string>('');
  readonly showIcon = input<boolean>(true);
  readonly iconPosition = input<'left' | 'right'>('right');
  readonly icon = input<string>('heroicons_outline:rocket-launch');
  readonly showText = input<boolean>(true);
  readonly interactionMode = input<'both' | 'text-only' | 'icon-only'>('both');
  readonly customTooltipText = input<string | undefined>();

  readonly tripNavigate = output<TripNavigationEvent>();

  readonly isNavigable = computed(() =>
    this.tripNavService.isTripNavigable(this.trip())
  );

  readonly displayText = computed(() => {
    const t = this.trip();
    if (!t) return '-';

    const id = t.id != null ? `${t.id}` : null;
    const code = t.externalKeys?.code;

    switch (this.displayMode()) {
      case 'id':
        return id || '-';
      case 'code':
        return code || id || '-';
      case 'both':
        if (id && code) return `${id} - ${code}`;
        return id || code || '-';
      default:
        return id || '-';
    }
  });

  readonly tooltipText = computed(() => {
    if (!this.isNavigable()) return '';
    return this.customTooltipText() || this.tripNavService.getNavigationDescription();
  });

  readonly navigationConfig = computed<NavigationConfig<TripNavigationData, TripQueryParams>>(() => {
    const trip = this.trip();
    return {
      data: trip,
      url: ['monitor', 'principal-screen'],
      // The monitor screen matches trips by their NUMERIC id, not the Mongo _id.
      queryParams: { tripId: trip?.id != null ? String(trip.id) : undefined, ...this.customQueryParams() },
      openInNewTab: this.openInNewTab(),
    };
  });

  readonly containerClasses = computed(() => {
    const baseClasses = 'trip-navigation';
    const navigableClasses = this.isNavigable() ? 'navigable' : 'non-navigable';
    return [baseClasses, navigableClasses, this.customClass()]
      .filter(cls => cls.length > 0)
      .join(' ');
  });

  handleNavigation(navigationEvent: NavigationEvent<TripNavigationData, TripQueryParams>): void {
    if (!this.isNavigable()) return;

    const tripNavigationEvent: TripNavigationEvent = {
      data: navigationEvent.config.data,
      queryParams: navigationEvent.config.queryParams || {},
      timestamp: navigationEvent.timestamp,
    };

    this.tripNavigate.emit(tripNavigationEvent);

    this.tripNavService.navigateToTrip(
      navigationEvent.config.data,
      navigationEvent.config.queryParams,
      navigationEvent.config.openInNewTab,
    );
  }
}
